import { api } from '@packages/backend/convex/_generated/api';
import type { CvxReturnType } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useEffect, useLayoutEffect, useState } from 'react';
import { z } from 'zod';
import { ActionButton } from '@/components/ActionButton';
import { ColorBadge } from '@/components/ColorBadge';
import { LoadingIcon } from '@/components/LoadingIcon';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useRestaurant } from '@/routes/restaurant/useRestaurantData';

export const Route = createFileRoute('/r/$restaurantId/s/stripe')({
  validateSearch: z.object({
    /**
     * Stripe `refresh_url` used by `accountLinks.create()`.
     * The URL the user will be redirected to if the account link is expired, has been previously-visited, or is otherwise invalid.
     * https://docs.stripe.com/api/account_links/create#create_account_link-refresh_url
     */
    refresh: z.boolean().optional(),
  }),
  component: StripePage,
});

function StripePage() {
  const { refresh } = Route.useSearch();
  const { restaurantId } = Route.useParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const convex = useConvex();
  const restaurant = useRestaurant();
  const [stripeAccountStatus, setStripeAccountStatus] =
    useState<CvxReturnType<typeof api.stripe_node.getAccountStatus>>();

  useEffect(() => {
    void convex
      .action(api.stripe_node.getAccountStatus, {
        restaurantId,
      })
      .then(setStripeAccountStatus);
  }, []);

  // Check if ?refresh=true param is present in URL
  // If so, then call handle start or continue stripe onboarding.
  useLayoutEffect(() => {
    if (refresh) {
      console.log('Refreshing stripe onboarding...');
      void handleStartOrContinueStripeOnboarding();
    }
  }, [refresh]);

  const handleStartOrContinueStripeOnboarding = async () => {
    setIsLoading(true);
    // window.location.href = await getAccountLinkUrl();
    const returnUrl = getCurrentPageUrl();
    const accountLink = await convex.action(api.stripe_node.getStripeAccountLinkUrl, {
      restaurantId,
      returnUrl,
    });
    window.location.href = accountLink.accountLinkUrl;
    setIsLoading(false);
  };

  if (refresh) return <LoadingIcon />;
  if (stripeAccountStatus === undefined) return <LoadingIcon />;

  if (stripeAccountStatus.status === 'NOT_FOUND') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Stripe Payments Integration</CardTitle>
          <CardDescription>
            Setup a Stripe account to start accepting payments online.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ActionButton loading={isLoading} onClick={handleStartOrContinueStripeOnboarding}>
            Setup Stripe Account
          </ActionButton>
        </CardContent>
      </Card>
    );
  }

  if (stripeAccountStatus.status === 'IN_PROGRESS') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className={'flex gap-2'}>
            Stripe Payments Integration
            <ColorBadge color="blue">In progress</ColorBadge>
          </CardTitle>
          <CardDescription>
            Finish setting up your Stripe account to start accepting payments online.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/*<div className="grid gap-4">{stripeAccountId}</div>*/}

          <ActionButton loading={isLoading} onClick={handleStartOrContinueStripeOnboarding}>
            Continue Stripe Setup
          </ActionButton>
        </CardContent>
      </Card>
    );
  }

  if (stripeAccountStatus.status === 'COMPLETE') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className={'flex gap-2'}>
            Stripe Payments Integration
            <ColorBadge color="green">Connected</ColorBadge>
          </CardTitle>

          <CardDescription>{`Stripe Account ID: ${restaurant.stripeConnectAccountId}`}</CardDescription>
        </CardHeader>
        <CardContent>
          <ActionButton onClick={openStripeDashboard}>Login to Stripe</ActionButton>
        </CardContent>
      </Card>
    );
  }
}

const openStripeDashboard = () => {
  // Specify the URL you want to open in a new window or tab
  const stripeURL = 'https://dashboard.stripe.com/login';

  // Open the URL in a new window with optional settings
  const newWindow = window.open(stripeURL, '_blank');

  // Check if the new window/tab was successfully opened
  if (newWindow) {
    newWindow.focus();
  } else {
    // Handle the case where the browser's pop-up blocker prevented the new window from opening
    alert('\nUnable to open the Stripe dashboard.\n\nPlease check your pop-up blocker settings.');
  }
};

/**
 * Get the URL of the current page (without any query parameters).
 */
function getCurrentPageUrl(): string {
  return `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
}
